<template>
    <Accordion :pt="accordionOptions">
        <AccordionTab :header="i18n('offer.orderApplications')">
            <div
                v-for="orderApp of roomOrderApplications"
                :key="orderApp.id"
                class="room-order-applications-list-item row"
            >
                <div class="col-md-9 order-app-name">{{ orderApp.name }}</div>
                <div class="col-md-3">
                    <button
                        v-if="!hasActiveOffer(orderApp)"
                        class="btn btn-sm btn-clean pull-right"
                        :disabled="props.disabled"
                        @click="activateOffer(orderApp)"
                    >
                        {{ i18n("offer.activate") }}
                    </button>

                    <button
                        v-if="hasActiveOffer(orderApp)"
                        class="btn btn-sm btn-clean pull-right"
                        :disabled="props.disabled"
                        @click="deactivateOffer(orderApp)"
                    >
                        {{ i18n("offer.deactivate") }}
                    </button>
                </div>
            </div>
        </AccordionTab>
    </Accordion>
</template>

<script setup lang="ts">
    import type { IOfferWithScheduleState } from "@/data/model/Offer";
    import type { IRoomOrderApplication, IRoomWithService } from "@/data/model/Room";
    import { getRoomOrderApplications, isOfferActivated } from "@/data/services/OfferPlanningService";
    import { i18n } from "@/portal/Trans";
    import { translate } from "@/shared/localization/language";
    import { computed } from "vue";

    import Accordion from "primevue/accordion";
    import AccordionTab from "primevue/accordiontab";

    const props = defineProps<{
        room: IRoomWithService;
        offer: IOfferWithScheduleState;
        disabled: boolean;
    }>();

    const emit = defineEmits(["onActivateClick", "onDeactivateClick"]);

    const accordionOptions = {
        root: {
            class: "room-order-applications-accordion",
        },
    };

    const roomOrderApplications = computed(() => {
        const allOrderApplications = getRoomOrderApplications(props.room);
        const result = allOrderApplications.map((x) => ({
            id: x.id,
            name: translate(x.name),
        }));

        // sort by name
        result.sort((a, b) => (a.name > b.name ? 1 : -1));

        return result;
    });

    function hasActiveOffer(orderApp: IRoomOrderApplication) {
        return isOfferActivated(props.offer, props.room.id, orderApp.id);
    }

    function activateOffer(orderApp: IRoomOrderApplication) {
        emit("onActivateClick", orderApp);
    }

    function deactivateOffer(orderApp: IRoomOrderApplication) {
        emit("onDeactivateClick", orderApp);
    }
</script>

<style lang="scss">
    .room-order-applications-accordion {
        margin-bottom: 0.25em;
        margin-top: -1.25em;

        .p-accordion-header {
            .p-accordion-header-link {
                display: inline-block;
                padding: 0rem 0.25rem !important;
                padding-right: 1rem !important;
                border-radius: 0 !important;
                border: none !important;
                background: none !important;
                line-height: 1.2em !important;

                .p-accordion-header-text {
                    line-height: 1.5em !important;
                    display: inline-block !important;
                    border-bottom: 1px solid rgba(0, 0, 0, 0) !important;
                    padding: 0.1em 0.25em !important;
                    margin-left: -0.25em !important;
                    padding-left: 0.2em !important;
                    transition: 0.2s;
                    font-weight: 500;
                    font-size: 0.95em;
                }

                &:hover {
                    .p-accordion-header-text {
                        border-bottom: 1px solid rgba(73, 80, 87, 0.3) !important;
                    }
                }
            }

            .p-icon {
                width: 0.8rem !important;
                height: 0.8rem !important;
            }
        }

        .p-accordion-content {
            border: none !important;
            padding: 0 !important;
            padding-left: 1.7em !important;
            padding-top: 0.5em !important;
        }
    }
</style>
